/**
 * AI Chatbot Service - Bolt
 * 
 * Handles communication with the AI chat API endpoints
 */

import { getAuthToken } from '../utils/authUtils';

// Use Cloud Function URL for AI endpoints
const AI_API_BASE_URL = process.env.REACT_APP_AI_API_URL || 
  `${process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || ''}/aiChat`;

/**
 * Send a message to the AI chatbot
 * @param {string} message - The user's message
 * @param {Array} conversationHistory - Previous messages in the conversation
 * @param {string} facilityId - Optional: The current facility ID for scoping searches
 * @returns {Promise<{message: string, conversationId: string|null}>}
 */
export const sendMessage = async (message, conversationHistory = [], facilityId = null) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${AI_API_BASE_URL}/ai/chat`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        conversationHistory,
        facilityId, // Pass current facility for proper data scoping
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send message');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Service Error:', error);
    throw error;
  }
};

/**
 * Send a message and receive a streaming response
 * @param {string} message - The user's message
 * @param {Array} conversationHistory - Previous messages in the conversation
 * @param {function} onChunk - Callback for each text chunk received
 * @param {function} onComplete - Callback when streaming is complete
 * @param {function} onError - Callback for errors
 * @param {string} facilityId - Optional: The current facility ID for scoping searches
 * @returns {Promise<void>}
 */
export const sendMessageStream = async (
  message,
  conversationHistory = [],
  onChunk,
  onComplete,
  onError,
  facilityId = null
) => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${AI_API_BASE_URL}/ai/chat/stream`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        conversationHistory,
        facilityId, // Pass current facility for proper data scoping
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send message');
    }
    
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        onComplete?.();
        break;
      }
      
      buffer += decoder.decode(value, { stream: true });
      
      // Process SSE events
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.text) {
              onChunk?.(data.text);
            }
            
            if (data.error) {
              onError?.(new Error(data.error));
            }
          } catch (parseError) {
            console.error('Failed to parse SSE data:', parseError);
          }
        }
      }
    }
  } catch (error) {
    console.error('AI Stream Error:', error);
    onError?.(error);
  }
};

/**
 * Get AI capabilities based on user role
 * @returns {Promise<{queryCapabilities: Array, actionCapabilities: Array, userCanPerformActions: boolean}>}
 */
export const getCapabilities = async () => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${AI_API_BASE_URL}/ai/capabilities`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to get capabilities');
    }
    
    return await response.json();
  } catch (error) {
    console.error('AI Capabilities Error:', error);
    throw error;
  }
};

/**
 * Scan a document (driver's license or insurance card) and extract data using OCR
 * @param {string} imageBase64 - Base64-encoded image string (without data URI prefix)
 * @param {'drivers_license' | 'insurance_card'} documentType - Type of document to scan
 * @param {string} mimeType - MIME type of the image (default: 'image/jpeg')
 * @returns {Promise<{documentType: string, data: object, confidence: number, analyzedAt: string}>}
 */
export const scanDocument = async (imageBase64, documentType, mimeType = 'image/jpeg') => {
  try {
    const token = await getAuthToken();
    
    const response = await fetch(`${AI_API_BASE_URL}/ai/ocr/document`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        image: imageBase64,
        documentType,
        mimeType,
      }),
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to scan document');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Document Scan Error:', error);
    throw error;
  }
};

/**
 * Sample prompts for users to try
 */
export const SAMPLE_PROMPTS = {
  queries: [
    'Show me today\'s appointments',
    'Who are the patients scheduled for Dr. Smith?',
    'What are the pending lab orders?',
    'Show me recent encounters',
    'Which staff members are on duty today?',
  ],
  actions: [
    'Find patient John Doe',
    'Show billing status for patient ID abc123',
    'What is Dr. Johnson\'s schedule this week?',
    'List all cancelled appointments today',
  ],
};

const aiService = {
  sendMessage,
  sendMessageStream,
  getCapabilities,
  scanDocument,
  SAMPLE_PROMPTS,
};

export default aiService;
